import create from "zustand";
import { DistributionLocation, SuggestedRoute } from "module/eddm/dto";
import { TargetPayload } from "shared/models";
import {
  CampaignType,
  ClientType,
  CostsCalculationDataType,
  CountryType,
  ErrorsType,
  MapType,
  MissionD2DType,
  MissionH2HType,
  StoreType,
  UserType,
  AddressType,
  FlyerBuilderType,
  QRCodeSettingsType,
  CouponsListType,
} from "./types";

interface ImageState {
  imageState: {
    photos: any[];
    search: string;
    currentPage: number;
    hasMore: boolean;
    loading: boolean;
    orientation: string;
    anchorEl: HTMLElement | null;
    isHovered: boolean;
  };
  setImageState: (update: Partial<ImageState["imageState"]>) => void;
}

// @ts-ignore
const useStore = create<StoreType & ImageState>((set) => ({
  campaign: {} as CampaignType,
  client: {} as ClientType,
  costsCalculationData: {} as CostsCalculationDataType,
  errors: {} as ErrorsType,
  city: null,
  country: {} as CountryType,
  user: {} as UserType,
  map: { zoom: 12 } as MapType,
  missions: [] as MissionH2HType[] | MissionD2DType,
  timezone: "",
  hoveredMission: "",
  hoveredRoute: {} as any,
  flyerBuilder: { loading: false } as FlyerBuilderType,
  qrCodeSettings: {} as QRCodeSettingsType,
  couponsList: [] as CouponsListType[],
  distributionLocations: [] as DistributionLocation[],
  bgForTemplate: "",
  isSnapMode: true as boolean,
  currentFormat: "",
  currentFormatConfig: {},
  currentOrientations: "",
  isOrientationChanging: false as boolean,
  isUndoOrientationSync: false as boolean,
  isAddressBoxVisible: false as boolean,
  measurementUnit: "",
  showImagePanel: false as boolean,
  showIconsPanel: false as boolean,
  showQRcodePanel: false as boolean,
  brandKitAssetsPanel: null as "image" | "logo" | "icon" | null,
  imageState: {
    photos: [],
    search: "",
    currentPage: 1,
    hasMore: true,
    loading: false,
    orientation: "",
    anchorEl: null,
    isHovered: false,
  },
  selectedTargets: [] as TargetPayload[],
  suggestionsEnabled: null as boolean | null,
  suggestedRoutes: [] as SuggestedRoute[],
  dismissedSuggestedRoutes: [] as SuggestedRoute[],
  isD2D: false as boolean,
  initData: (data) => {
    // @ts-ignore
    set((state) => {
      const newState = { ...state, ...data };

      // Initialize measurement unit based on user locale if not already set
      if (!newState.measurementUnit || newState.measurementUnit === "") {
        const storedUnit = localStorage.getItem("measurementUnit");
        if (storedUnit && (storedUnit === "inches" || storedUnit === "mm")) {
          newState.measurementUnit = storedUnit;
        } else {
          // Use inline locale detection to avoid circular dependencies
          const detectUnit = (): "inches" | "mm" => {
            try {
              const userLocale = navigator.language || navigator.languages?.[0] || "en-US";
              const locale = new Intl.Locale(userLocale);
              const region = locale.region?.toUpperCase();

              // Imperial regions for design/printing context
              const imperialRegions = ["US", "LR", "MM"];
              const mixedRegions = ["CA"]; // Canada uses both, but many prefer inches for design/print

              if (region && (imperialRegions.includes(region) || mixedRegions.includes(region))) {
                return "inches";
              }

              return "mm";
            } catch (error) {
              // Fallback detection
              const userLocale = navigator.language || navigator.languages?.[0] || "en-US";
              return userLocale.includes("en-US") || userLocale.includes("en-CA") ? "inches" : "mm";
            }
          };

          const defaultUnit = detectUnit();
          newState.measurementUnit = defaultUnit;
          localStorage.setItem("measurementUnit", defaultUnit);
        }
      }

      return newState;
    });
  },

  updateUser: (data) => {
    set((state: StoreType) => ({
      user: {
        ...state.user,
        ...data,
      },
    }));
  },

  updateCostsCalculationData: (data) => {
    set((state) => ({
      costsCalculationData: {
        ...state.costsCalculationData,
        ...data,
      },
    }));
  },

  setCity: (data) => {
    // @ts-ignore
    set(() => (data ? { city: data } : { city: null, missions: [] }));
  },

  updateCity: (data) => {
    // @ts-ignore
    set((state) =>
      state.city
        ? {
            city: {
              ...state.city,
              ...data,
            },
          }
        : null
    );
  },

  updateErrors: (data) => {
    set((state) => ({
      errors: {
        ...state.errors,
        ...data,
      },
    }));
  },

  updateCampaign: (data) => {
    set((state) => ({
      campaign: {
        ...state.campaign,
        ...data,
      },
    }));
  },

  updateClient: (data) => {
    set((state) => ({
      client: {
        ...state.client,
        ...data,
      },
    }));
  },

  updateCountry: (data) => {
    set((state) => ({
      country: {
        ...state.country,
        ...data,
      },
    }));
  },

  updateQrCodeSettings: (data) => {
    set((state) => ({
      qrCodeSettings: {
        ...state.qrCodeSettings,
        ...data,
      },
    }));
  },

  setAddresses: (data) => {
    // @ts-ignore
    set((state) => {
      if (state.city?.id) {
        return {
          city: {
            ...state.city,
            addresses: data,
          },
        };
      }
    });
  },

  updateAddress: (data) => {
    // @ts-ignore
    set((state) => {
      if (state.city?.id) {
        return {
          city: {
            ...state.city,
            addresses: state.city.addresses.map((address: AddressType) =>
              data.id === address.id ? { ...address, ...data } : address
            ),
          },
        };
      }
    });
  },

  setDistributionLocations: (data: DistributionLocation[]) => {
    set(() => {
      return {
        distributionLocations: [...data],
      };
    });
  },

  setSuggestionsEnabled: (data: boolean) => {
    set((state) => ({
      ...state,
      suggestionsEnabled: data,
    }));
  },

  setSuggestedRoutes: (data: SuggestedRoute[]) => {
    set((state) => ({
      ...state,
      suggestedRoutes: data,
    }));
  },

  setDismissedSuggestedRoutes: (data: SuggestedRoute[]) => {
    set((state) => ({
      ...state,
      dismissedSuggestedRoutes: data,
    }));
  },

  setFocusedLocation: (location: DistributionLocation) => {
    // @ts-ignore
    set((state) => {
      const newLocations = state.distributionLocations.reduce(
        (acc: DistributionLocation[], curr: DistributionLocation) => {
          if (curr.name === location.name) {
            acc.push({ ...curr, isFocused: true });
          } else {
            acc.push({ ...curr, isFocused: false });
          }
          return acc;
        },
        []
      );
      return {
        distributionLocations: [...newLocations],
      };
    });
  },

  onAddressClick: (data) => {
    // @ts-ignore
    set((state) => {
      // @ts-ignore
      if (state.city?.id) {
        return {
          city: {
            ...state.city,
            addresses: state.city.addresses.map((address) =>
              data.id === address.id ? { ...address, editMode: true } : { ...address, editMode: false }
            ),
          },
        };
      }
    });
  },

  updateMap: (data) => {
    set((state) => ({
      map: {
        ...state.map,
        ...data,
      },
    }));
  },

  setMissions: (data) => {
    set(() => ({
      missions: data,
    }));
  },

  setTimezone: (timezone) => {
    set(() => ({
      timezone,
    }));
  },

  setHoveredMission: (hoveredMission) => {
    set(() => ({
      hoveredMission,
    }));
  },

  setHoveredRoute: (hoveredRoute) => {
    set(() => ({
      hoveredRoute,
    }));
  },

  setAvailableCities: (data) => {
    set((state) => ({
      country: { ...state.country, citiesList: data },
    }));
  },

  updateFlyerBuilder: (data) => {
    set((state) => ({
      flyerBuilder: { ...state.flyerBuilder, ...data },
    }));
  },

  updateCouponsList: (data) => {
    set((state) => ({
      couponsList: {
        ...state.couponsList,
        ...data,
      },
    }));
  },
  setBgForTemplate: (data: string) => {
    set((state) => ({
      ...state,
      bgForTemplate: data,
    }));
  },

  setIsSnapMode: (data: boolean) => {
    set((state) => ({
      ...state,
      isSnapMode: data,
    }));
  },

  setCurrentFormat: (data: string) => {
    set((state) => ({
      ...state,
      currentFormat: data,
    }));
  },

  setCurrentFormatConfig: (config) => set({ currentFormatConfig: config }),

  setCurrentOrientations: (data: string) => {
    set((state) => ({
      ...state,
      currentOrientations: data,
    }));
  },

  setIsOrientationChanging: (data: boolean) => {
    set((state) => ({
      ...state,
      isOrientationChanging: data,
    }));
  },

  setIsUndoOrientationSync: (data: boolean) => {
    set((state) => ({
      ...state,
      isUndoOrientationSync: data,
    }));
  },

  setMeasurementUnit: (data: string) => {
    set((state) => ({
      ...state,
      measurementUnit: data,
    }));
  },

  setIsAddressBoxVisible: (data: boolean) => {
    set((state) => ({
      ...state,
      isAddressBoxVisible: data,
    }));
  },

  setShowImagePanel: (data: boolean) => {
    set((state) => ({
      ...state,
      showImagePanel: data,
    }));
  },

  setShowIconsPanel: (data: boolean) => {
    set((state) => ({
      ...state,
      showIconsPanel: data,
    }));
  },

  setShowQRcodePanel: (data: boolean) => {
    set((state) => ({
      ...state,
      showQRcodePanel: data,
    }));
  },

  setBrandKitAssetsPanel: (data: "image" | "logo" | "icon" | null) => {
    set((state) => ({
      ...state,
      brandKitAssetsPanel: data,
    }));
  },

  setImageState: (update) =>
    set((state) => ({
      imageState: { ...state.imageState, ...update },
    })),

  updateSelectedTargets: (data: TargetPayload[]) => {
    set((state) => ({
      ...state,
      selectedTargets: data,
    }));
  },
}));

export { useStore };
